import { useEffect, useRef } from 'react';
import {User} from '@supabase/supabase-js';

import { supabase } from '../lib/supabase';
import { useAuthStore } from '../stores/auth.store';
export type { AuthUser } from '../stores/auth.store';
 
export const useAuth = () => {
  const {
    user,
    session,
    loading,
    error,
    signInWithEmail,
    signUpWithEmail,
    signInWithProvider,
    signOut,
    setError,
  } = useAuthStore();

  const profileChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  

  const setUser = useAuthStore((s) => s.setUser);
  const setLoading = useAuthStore((s) => s.setLoading);
  const setSession = useAuthStore((s) => s.setSession);

  useEffect(() => {
    const initSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) console.error('Error fetching session:', error.message);
        setSession(data?.session ?? null);
        const supaUser = data?.session?.user;
        setUser(supaUser ? {
          id: supaUser.id,
          email: supaUser.email || '',
          name: supaUser.user_metadata?.name || supaUser.email || '',
          provider: supaUser.app_metadata?.provider || 'email',
        } : null);
      } catch {
        setUser(null);
        setSession(null);
      } finally {
        setLoading(false);
      }
    };
    initSession();
  
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session ?? null);
      const supaUser = session?.user;
      setUser(supaUser ? {
        id: supaUser.id,
        email: supaUser.email || '',
        name: supaUser.user_metadata?.name || supaUser.email || '',
        provider: supaUser.app_metadata?.provider || 'email',
      } : null);
      setLoading(false);
    });
    return () => subscription.unsubscribe();
  }, [setUser, setSession, setLoading]);
  

useEffect(() => {
  if (!user) {
    if (profileChannelRef.current) {
      supabase.removeChannel(profileChannelRef.current);
      profileChannelRef.current = null;
    }
    return;
  }
  if (!profileChannelRef.current) {
    const channel = supabase
      .channel(`profile-delete-${user.id}`)
      .on('postgres_changes', {
        event: 'DELETE',
        schema: 'public',
        table: 'profiles',
        filter: `id=eq.${user.id}`,
      }, () => {
        useAuthStore.getState().signOut();
      })
      .subscribe();
    profileChannelRef.current = channel;
  }
  return () => {
    if (profileChannelRef.current) {
      supabase.removeChannel(profileChannelRef.current);
      profileChannelRef.current = null;
    }
  };
}, [user]);

// In useAuth.ts, add this useEffect
useEffect(() => {
  if (user?.id) {
    const userForProfile = {
      id: user.id,
      email: user.email,
      app_metadata: { provider: user.provider },
      user_metadata: { name: user.name },
      aud: 'authenticated',
      created_at: new Date().toISOString(),
    } as User;
    useAuthStore.getState().createOrUpdateProfile(userForProfile);
  }
}, [user?.id]);




  return {
    user,
    session,
    loading,
    error,
    signInWithEmail,
    signUpWithEmail,
    signInWithProvider,
    signOut,
    setError
  };
};
